import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import {
  HoverHintDefinitionGrid,
  type HoverHintDefinitionRow,
} from "@/components/ui/HoverHintDefinitionGrid";
import { fetchSupplierDetail } from "@/lib/masterData";

import type { SupplierPeekContext } from "./entityPeekTypes";

export const supplierDetailQueryKey = (supplierId: number) => ["mrp", "supplier", supplierId] as const;

const SUPPLIER_DETAIL_STALE_MS = 30_000;

function readSupplierStringField(supplier: Record<string, unknown>, key: string): string | null {
  const raw = supplier[key];
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

function buildSupplierPeekRows(
  supplier: Record<string, unknown>,
  context?: SupplierPeekContext,
): HoverHintDefinitionRow[] {
  const rows: HoverHintDefinitionRow[] = [];
  const name =
    readSupplierStringField(supplier, "mv_supplier_name") ??
    context?.supplier_name?.trim() ??
    null;
  if (name != null) {
    rows.push({ label: "Supplier", value: name });
  }
  const currency = readSupplierStringField(supplier, "currency");
  if (currency != null) {
    rows.push({ label: "Currency", value: currency });
  }
  const paymentTerms = readSupplierStringField(supplier, "payment_terms");
  if (paymentTerms != null) {
    rows.push({ label: "Payment terms", value: paymentTerms });
  }
  const email = readSupplierStringField(supplier, "mv_supplier_email");
  if (email != null) {
    rows.push({ label: "Email", value: email });
  }
  return rows;
}

export function SupplierDetailHoverHint({
  supplierId,
  context,
  emptyMessage,
}: {
  supplierId: number;
  context?: SupplierPeekContext;
  emptyMessage?: string;
}): JSX.Element {
  const detailQuery = useQuery({
    queryKey: supplierDetailQueryKey(supplierId),
    queryFn: () => fetchSupplierDetail(supplierId),
    staleTime: SUPPLIER_DETAIL_STALE_MS,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading supplier…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data == null) {
    const fallbackName = context?.supplier_name?.trim();
    if (fallbackName != null && fallbackName !== "") {
      return <HoverHintDefinitionGrid rows={[{ label: "Supplier", value: fallbackName }]} />;
    }
    return <p className="text-xs text-slate-600">{emptyMessage ?? "Could not load supplier preview."}</p>;
  }

  const rows = buildSupplierPeekRows(detailQuery.data.supplier, context);
  if (rows.length === 0) {
    return <p className="text-xs text-slate-600">{emptyMessage ?? "No supplier details."}</p>;
  }

  return <HoverHintDefinitionGrid rows={rows} />;
}
